/**
 * ─────────────────────────────────────────────────────────────────────────
 * Mindful Sips — Hero Section JS
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Responsibilities:
 *   1. Poster → video crossfade  (video loads behind poster, fades in when ready)
 *   2. prefers-reduced-motion    (keeps poster, never plays video)
 *   3. Smooth anchor scroll      ("How It Works" → #purpose-framework)
 *   4. Intersection-based play   (pause video when hero is off-screen, resume on re-entry)
 *
 * No dependencies. Vanilla JS, ES2017+.
 * Safe to defer or place before </body>.
 * ─────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ── 1. Reduced-motion check ──────────────────────────────────────── */
  const prefersReducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 2. Element refs ──────────────────────────────────────────────── */
  const hero   = document.querySelector('.ms-hero');
  const visual = hero && hero.querySelector('.ms-hero__visual');
  const video  = hero && hero.querySelector('.ms-hero__video');
  const poster = hero && hero.querySelector('.ms-hero__poster');

  if (!hero || !visual || !video) return; // section not present on this page

  /* ── 3. Reduced-motion: keep poster, disable video entirely ───────── */
  if (prefersReducedMotion) {
    video.removeAttribute('autoplay');
    video.pause && video.pause();
    // CSS already hides the video element via media query;
    // this ensures autoplay never fires even if CSS is slow to parse.
    return;
  }

  /* ── 4. Poster → video crossfade ─────────────────────────────────── */
  /**
   * The <video> starts with opacity:0. When the browser has buffered
   * enough to play (canplay event), we:
   *   a) add .is-ready  → CSS transition fades video in
   *   b) add .video-playing on the wrapper → CSS fades poster out
   *
   * This means the visitor always sees the poster immediately (it's a
   * lightweight JPG), and the video takes over silently once ready.
   * On slow connections they just keep seeing the poster — no spinner,
   * no blank frame, no layout shift.
   */
  function onVideoReady() {
    video.classList.add('is-ready');
    visual.classList.add('video-playing');
    video.removeEventListener('canplay', onVideoReady);
  }

  video.addEventListener('canplay', onVideoReady);

  /* Safety: if canplay already fired before listener attached */
  if (video.readyState >= 3) {
    onVideoReady();
  }

  /* ── 5. Intersection observer — pause off-screen, resume on-screen ── */
  /**
   * Pausing off-screen saves CPU/battery on mobile and prevents
   * a jerky loop beat when the user scrolls back up.
   */
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            /* Resume only if it was playing before */
            if (video.paused && !prefersReducedMotion) {
              video.play().catch(function () {
                /*
                 * play() can reject if autoplay policy blocks it
                 * (e.g. some mobile browsers without muted).
                 * The video is already muted + playsinline so this
                 * should not happen, but we catch silently.
                 */
              });
            }
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.1, // pause when < 10% of hero is visible
      }
    );

    observer.observe(hero);
  }

  /* ── 6. Smooth anchor scroll for "How It Works" ───────────────────── */
  /**
   * The ghost CTA has data-scroll-to="purpose-framework".
   * Clicking it scrolls to #purpose-framework smoothly,
   * with a small offset so the section header isn't hidden
   * behind a sticky nav bar (adjust OFFSET_PX to match your nav height).
   */
  var OFFSET_PX = 80; // matches the nav height in home-mockup.html

  document.querySelectorAll('[data-scroll-to]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      var targetId = trigger.getAttribute('data-scroll-to');
      var target   = document.getElementById(targetId);

      if (!target) return; // let the default href handle it if element missing

      e.preventDefault();

      var targetTop =
        target.getBoundingClientRect().top + window.pageYOffset - OFFSET_PX;

      window.scrollTo({
        top:      targetTop,
        behavior: 'smooth',
      });

      /*
       * Update URL hash without triggering a jump.
       * history.pushState is used so back-button works correctly.
       */
      if (history.pushState) {
        history.pushState(null, '', '#' + targetId);
      }
    });
  });

})();
